import {
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuProps,
  withStyles,
} from "@material-ui/core";
import ListItemText from "@material-ui/core/ListItemText";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import LanguageIcon from "@material-ui/icons/Language";
import i18n from "i18next";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useStore } from "../../hooks/useStore";
import { useTranslation } from "../../hooks/useTranslation";
import { logout } from "../../redux/actions/authActions";
import { configOnEditing } from "../../redux/actions/configActions";
import { languageOptions, RoutePaths } from "../../utils/Items";
import DialogConfirm from "../common/DialogConfirm";
import SimplePopover from "../common/SimplePopover";
import { useStyles } from "./MenuOptionsStyle";
import {handleActiveElement} from "../../utils/validations/keyboardShortcutsValidation";

const StyledMenu = withStyles({
  paper: {
    border: "1px solid #d3d4d5",
  },
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "center",
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const MenuOptions = () => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorCloseSession, setAnchorCloseSession] =
    React.useState<HTMLElement | null>(null);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t, BundleKey, changeLanguage } = useTranslation();
  const openPopover = Boolean(anchorCloseSession);
  const { configStore } = useStore();
  const history = useHistory();

  useEffect(() => {
    document.addEventListener("keydown", redirectKeyboardSignOut);
    return () => {
      document.removeEventListener("keydown", redirectKeyboardSignOut);
    };
  }, []);

  const redirectKeyboardSignOut = (e: any) => {
    if (configStore.modalEditing.is) {
      dispatch(
        configOnEditing({
          show: true,
          action: () => {
            history.push(RoutePaths.Dashboard);
          },
        })
      );
    } else {
      if ((e.keyCode === 48 && e.altKey) || (e.keyCode === 96 && e.altKey)) {
        const activeElement: any = document.activeElement;
        handleActiveElement({
          activeElement: activeElement,
          action: () => setOpenDialog(true)
        })
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirm = () => {
    dispatch(logout());
    setOpenDialog(false);
  };

  const handleChangeLang = (lang: string) => {
    changeLanguage(lang);
    handleClose();
  };

  const handlePopoverClose = () => {
    setAnchorCloseSession(null);
  };

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setAnchorCloseSession(event.currentTarget);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <div className={classes.flexContainer} style={{ width: "100%" }}>
        <Button
          className={classes.flexItem}
          startIcon={<LanguageIcon />}
          fullWidth
          color="primary"
          onClick={handleClick}
        >
          {t(BundleKey.LANG)}
        </Button>
        <StyledMenu
          id="select-language"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {languageOptions.map((item, index: any) => (
            <StyledMenuItem
              selected={item.id === i18n.language}
              key={index}
              onClick={() => handleChangeLang(item.id)}
            >
              <ListItemIcon>
                <img src={item.iconUrl} alt={item.bundleName} />
              </ListItemIcon>
              <ListItemText primary={t(item.bundleName)} />
            </StyledMenuItem>
          ))}
        </StyledMenu>

        <Button
          className={classes.flexItem}
          startIcon={<ExitToAppIcon />}
          fullWidth
          color="primary"
          onClick={handleClickOpen}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
        >
          {t(BundleKey.LOGOUT_TITLE)}
        </Button>
      </div>
      <SimplePopover
        title="ALT+0"
        handlePopoverClose={handlePopoverClose}
        open={openPopover}
        anchorEl={anchorCloseSession}
      />
      <DialogConfirm
        openDialog={openDialog}
        handleConfirm={handleConfirm}
        handleClose={handleCloseDialog}
        message={t(BundleKey.LOGOUT_MESSAGE)}
        title={t(BundleKey.LOGOUT_TITLE)}
        titleButtonNot={t(BundleKey.ACTION_NO)}
        titleButtonOk={t(BundleKey.ACTION_YES)}
      />
    </>
  );
};

export default MenuOptions;
